import { createQuery } from 'react-query-kit';

import { buildAgentName, convertJsonPathToFieldId, getEnrichmentSuggestion } from 'hooks/useGruAgent/helpers';
import { ActionResult } from 'hooks/useGruAgent/types';
import { getWorkflow, listWorkflows } from 'services/gru/gruApi';
import { logError } from 'services/logging/logging';

type EnrichmentVariables = {
  messageId?: string;
  partnerId?: string;
  isAdmin?: boolean;
  hasEnrichmentApplied?: boolean;
};

export type EnrichedPaths = Set<string> | undefined;

const ENRICHMENT_QUERY_KEY = 'gru-enriched-field-paths';

export const useGruEnrichedPaths = createQuery<EnrichedPaths, EnrichmentVariables, Error>({
  primaryKey: ENRICHMENT_QUERY_KEY,
  queryFn: async ({ queryKey: [, { messageId, partnerId, isAdmin }] }) => {
    const agentName = buildAgentName(messageId!, partnerId!, isAdmin ?? false);

    try {
      const workflows = await listWorkflows(agentName);
      const appliedWorkflows = workflows.filter((w) => w.applied_at !== null);
      if (appliedWorkflows.length === 0) {
        return undefined;
      }

      const workflowDetails = await Promise.all(appliedWorkflows.map((w) => getWorkflow(agentName, w.id)));

      const allPaths = new Set<string>();

      for (const detail of workflowDetails) {
        //TODO: Needs to be fixed - we don't need to spread the payload, we should have just have the correct types everywhere
        const actionResults = detail.actionResults.map((ar) => ({
          ...(ar.payload as ActionResult),
          id: ar.id,
        }));

        const enrichment = getEnrichmentSuggestion(actionResults);
        if (enrichment) {
          for (const item of enrichment.payload.enriched) {
            allPaths.add(convertJsonPathToFieldId(item.jsonpath));
          }
        }
      }

      return allPaths.size > 0 ? allPaths : undefined;
    } catch (error) {
      logError(error);
      return undefined;
    }
  },
  enabled: (_, { messageId, partnerId, hasEnrichmentApplied }) =>
    Boolean(messageId && partnerId && hasEnrichmentApplied),
});
